import {
  VALUE_FORMAT_DATE_PATTERN_MAX_LENGTH,
  VALUE_FORMAT_LOCALE,
  VALUE_FORMAT_MAX_DECIMAL_PLACES,
  VALUE_FORMAT_SYMBOL_POSITION,
  VALUE_FORMAT_SYMBOL_SPACING,
  VALUE_FORMAT_SYMBOLS,
  VALUE_FORMAT_TYPE,
} from "@/constants";
import {
  formatDateParts,
  hasDateToken,
  isEpochInputPattern,
  parseDateValue,
} from "@/shared/lib/datePattern";

/**
 * How a column's values are displayed: a table column, or a chart's axis, series or legend field.
 *
 * Presentation only. A format is applied where a value is drawn and nowhere else, so the query,
 * sorting, filtering, drill-downs and downloads all keep working on the raw value.
 */

export type ValueFormatSymbolPosition =
  (typeof VALUE_FORMAT_SYMBOL_POSITION)[keyof typeof VALUE_FORMAT_SYMBOL_POSITION];

export type NumberValueFormat = {
  type: typeof VALUE_FORMAT_TYPE.NUMBER;
  /** One of `VALUE_FORMAT_SYMBOLS`; absent adds none. Added as written, never scaling the value. */
  symbol?: string | null;
  /** Which side the symbol sits on; absent uses that symbol's usual side. */
  symbol_position?: ValueFormatSymbolPosition | null;
  /** Groups digits in thousands: 1650 shows as 1,650. */
  thousands_separator?: boolean;
  /** Digits fixed after the decimal point, rounding; absent shows the value's own. */
  decimal_places?: number | null;
};

export type DateValueFormat = {
  type: typeof VALUE_FORMAT_TYPE.DATE;
  /**
   * How a value is read, such as `DD/MM/YYYY`, or `X`/`x` for Unix seconds/milliseconds. Absent
   * reads ISO dates and timestamps, which is how date and timestamp columns arrive.
   */
  input_pattern?: string | null;
  /** How the date is shown, such as `MMM D, YYYY`. */
  output_pattern: string;
};

export type ValueFormat = NumberValueFormat | DateValueFormat;

/** The registry entry for `symbol`, or undefined for a symbol that is not offered. */
function symbolOption(symbol: string) {
  return VALUE_FORMAT_SYMBOLS.find((option) => option.symbol === symbol);
}

/** Whether `symbol` is one a number format may add. */
export function isValueFormatSymbol(symbol: unknown): symbol is string {
  return typeof symbol === "string" && symbolOption(symbol) !== undefined;
}

/** The side `symbol` sits on unless the author picks the other: `$` before, `%` after. */
export function defaultSymbolPosition(symbol: string): ValueFormatSymbolPosition {
  return symbolOption(symbol)?.defaultPosition ?? VALUE_FORMAT_SYMBOL_POSITION.PREFIX;
}

function isSymbolPosition(value: unknown): value is ValueFormatSymbolPosition {
  return (
    value === VALUE_FORMAT_SYMBOL_POSITION.PREFIX || value === VALUE_FORMAT_SYMBOL_POSITION.SUFFIX
  );
}

/** A usable date pattern: non-blank, not too long, and holding at least one token. */
export function isValidDatePattern(pattern: unknown, { input = false } = {}): pattern is string {
  if (typeof pattern !== "string") {
    return false;
  }
  const trimmed = pattern.trim();
  return (
    trimmed !== "" &&
    trimmed.length <= VALUE_FORMAT_DATE_PATTERN_MAX_LENGTH &&
    (hasDateToken(trimmed) || (input && isEpochInputPattern(trimmed)))
  );
}

/**
 * Whether `format` can be saved as the author set it. Only a date format can fail: its display
 * pattern needs a token, and an input pattern, when one is typed, needs one too (or `X`/`x`).
 * Saving it anyway would store no format at all -- `normalizeValueFormat` drops it -- so an editor
 * refuses to save instead of losing the author's choice without a word.
 */
export function isValueFormatUsable(format: ValueFormat | null | undefined): boolean {
  if (format === null || format === undefined || format.type !== VALUE_FORMAT_TYPE.DATE) {
    return true;
  }
  const inputPattern = format.input_pattern?.trim() ?? "";
  return (
    isValidDatePattern(format.output_pattern) &&
    (inputPattern === "" || isValidDatePattern(inputPattern, { input: true }))
  );
}

/**
 * `format` in the one shape it is stored and compared in, or null when it does nothing.
 *
 * A number format with no symbol, no separator and no fixed decimals would leave every value as it
 * was, so it is no format at all -- stored as none rather than as an empty object.
 */
export function normalizeValueFormat(format: ValueFormat | null | undefined): ValueFormat | null {
  if (format === null || format === undefined) {
    return null;
  }
  if (format.type === VALUE_FORMAT_TYPE.DATE) {
    const outputPattern = format.output_pattern.trim();
    if (!isValidDatePattern(outputPattern)) {
      return null;
    }
    const inputPattern = format.input_pattern?.trim() ?? "";
    return {
      type: VALUE_FORMAT_TYPE.DATE,
      ...(isValidDatePattern(inputPattern, { input: true })
        ? { input_pattern: inputPattern }
        : {}),
      output_pattern: outputPattern,
    };
  }
  const symbol = isValueFormatSymbol(format.symbol) ? format.symbol : null;
  const decimalPlaces =
    typeof format.decimal_places === "number" &&
    Number.isInteger(format.decimal_places) &&
    format.decimal_places >= 0 &&
    format.decimal_places <= VALUE_FORMAT_MAX_DECIMAL_PLACES
      ? format.decimal_places
      : null;
  const thousandsSeparator = format.thousands_separator === true;
  if (symbol === null && decimalPlaces === null && !thousandsSeparator) {
    return null;
  }
  return {
    type: VALUE_FORMAT_TYPE.NUMBER,
    ...(symbol !== null
      ? {
          symbol,
          symbol_position: isSymbolPosition(format.symbol_position)
            ? format.symbol_position
            : defaultSymbolPosition(symbol),
        }
      : {}),
    thousands_separator: thousandsSeparator,
    ...(decimalPlaces !== null ? { decimal_places: decimalPlaces } : {}),
  };
}

/**
 * A stored or submitted format read defensively: it arrives from the server, a raw-JSON edit or
 * the App Builder chat, so anything malformed is dropped rather than trusted. Null for no format.
 */
export function parseValueFormat(raw: unknown): ValueFormat | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (record.type === VALUE_FORMAT_TYPE.DATE) {
    return typeof record.output_pattern === "string"
      ? normalizeValueFormat({
          type: VALUE_FORMAT_TYPE.DATE,
          input_pattern: typeof record.input_pattern === "string" ? record.input_pattern : null,
          output_pattern: record.output_pattern,
        })
      : null;
  }
  if (record.type === VALUE_FORMAT_TYPE.NUMBER) {
    return normalizeValueFormat({
      type: VALUE_FORMAT_TYPE.NUMBER,
      symbol: typeof record.symbol === "string" ? record.symbol : null,
      symbol_position: isSymbolPosition(record.symbol_position) ? record.symbol_position : null,
      thousands_separator: record.thousands_separator === true,
      decimal_places: typeof record.decimal_places === "number" ? record.decimal_places : null,
    });
  }
  return null;
}

export function valueFormatsEqual(
  first: ValueFormat | null | undefined,
  second: ValueFormat | null | undefined,
): boolean {
  return (
    JSON.stringify(normalizeValueFormat(first)) === JSON.stringify(normalizeValueFormat(second))
  );
}

/** A plain decimal, optionally signed and in exponent form; what a NUMERIC column arrives as. */
const NUMERIC_TEXT = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;

/**
 * `value` as something `Intl.NumberFormat` can take, or null when it is not a number. Numeric text
 * stays text: a DECIMAL(38, 2) total can hold more digits than a double, and the formatter reads
 * text exactly where converting it first would round it.
 */
function numericInputOf(value: unknown): number | string | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (typeof value === "string") {
    const text = value.trim();
    return NUMERIC_TEXT.test(text) ? text : null;
  }
  return null;
}

/** Digits after the decimal point in plain numeric text, so `1650.50` keeps its trailing zero. */
function fractionDigitsOf(text: string): number {
  const match = /\.(\d+)$/.exec(text);
  return match === null ? 0 : match[1].length;
}

/** The most fraction digits `Intl.NumberFormat` accepts in every browser this app supports. */
const MAX_INTL_FRACTION_DIGITS = 20;

const numberFormatters = new Map<string, Intl.NumberFormat>();

function numberFormatter(grouping: boolean, minDigits: number, maxDigits: number) {
  const key = `${grouping}|${minDigits}|${maxDigits}`;
  let formatter = numberFormatters.get(key);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat(VALUE_FORMAT_LOCALE, {
      useGrouping: grouping,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
    });
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Formats numeric text without converting it to a double first. Intl.NumberFormat has read decimal
 * strings exactly since ES2023, which this project's ES2022 type library does not declare yet; an
 * engine without it converts the text to a number, so the worst case is the old rounding.
 */
function formatNumeric(formatter: Intl.NumberFormat, input: number | string): string {
  const format = formatter.format as (value: number | string) => string;
  return format.call(formatter, input);
}

/** `value` shown with a number format, or null when the value is not a number. */
export function formatNumberValue(value: unknown, format: NumberValueFormat): string | null {
  const input = numericInputOf(value);
  if (input === null) {
    return null;
  }
  const fixedDigits = format.decimal_places ?? null;
  const ownDigits = typeof input === "string" && !/e/i.test(input) ? fractionDigitsOf(input) : 0;
  const formatted = formatNumeric(
    numberFormatter(
      format.thousands_separator === true,
      fixedDigits ?? Math.min(ownDigits, MAX_INTL_FRACTION_DIGITS),
      fixedDigits ?? MAX_INTL_FRACTION_DIGITS,
    ),
    input,
  );
  const symbol = isValueFormatSymbol(format.symbol) ? format.symbol : null;
  if (symbol === null) {
    return formatted;
  }
  const position = isSymbolPosition(format.symbol_position)
    ? format.symbol_position
    : defaultSymbolPosition(symbol);
  if (position === VALUE_FORMAT_SYMBOL_POSITION.SUFFIX) {
    return `${formatted}${VALUE_FORMAT_SYMBOL_SPACING}${symbol}`;
  }
  // The sign leads, as accounting writes it: -$ 1,650 rather than $ -1,650.
  return formatted.startsWith("-")
    ? `-${symbol}${VALUE_FORMAT_SYMBOL_SPACING}${formatted.slice(1)}`
    : `${symbol}${VALUE_FORMAT_SYMBOL_SPACING}${formatted}`;
}

/** `value` shown with a date format, or null when it cannot be read as a date. */
export function formatDateValue(value: unknown, format: DateValueFormat): string | null {
  const parts = parseDateValue(value, format.input_pattern);
  return parts === null ? null : formatDateParts(parts, format.output_pattern);
}

/**
 * `value` as `format` displays it, or null when there is no format or it does not apply -- a
 * number format on text, or a date format on something that is not a date. The caller then shows
 * the value the way it would without a format, so a stray value is never hidden or mangled.
 */
export function formatValue(value: unknown, format: ValueFormat | null | undefined): string | null {
  if (format === null || format === undefined || value === null || value === undefined) {
    return null;
  }
  return format.type === VALUE_FORMAT_TYPE.DATE
    ? formatDateValue(value, format)
    : formatNumberValue(value, format);
}
